/**
 * RecommendationWidget — the chat's recommendation cards.
 *
 * Takes ONE entity (`referenceId`) or SEVERAL of the same type (`referenceIds`, ELARA-1063), dispatches
 * by `type` to a sub-component that owns its own reactive read, and renders each resolved entity
 * through its real card (`HotelCard`/`DestinationCard`/`PlaceCard`/`RoomCard`/`FlightCard`) — the same
 * treatment mobile's `RecommendationWidget` already gives every type (ELARA-1054, ELARA-1070).
 *
 * The count decides the shape, and the two are not interchangeable. One entity renders at `size="lg"`:
 * a leading title, the facts, then the photos as a scrollable strip. Several render as a `CarouselRow`
 * of `size="feature"` cards — a cover band with the text beneath — because at a third of the message
 * width that cover is modest and several entities are comparable at a glance, which is the point of
 * showing a set. The assistant's `body` describes the SET and renders once beneath the row.
 *
 * The `actions` corner takes the ICON actions — the shortlist heart every type gets, and the place
 * card's "Add to trip". On a `feature` card they sit ON the cover, where a narrow card has room to
 * spare. Labelled buttons go below the gallery instead, where the words are the point.
 *
 * The room card carries NO add or book action: both commit to a specific rate, and the card shows one
 * lead price. Its "View rates" button is the way to the guide, where the rates are. Hotel and
 * destination cards carry nothing at all — the card itself opens the guide.
 *
 * When an entity fails to load, its slot degrades to `RecommendationFallback` — prose only, no broken
 * card (ELARA-1071). In a carousel that is per slot, so one dead id does not take the row down.
 */
import type { DestinationsContainer } from '@elara/client-core/containers/DestinationsContainer';
import type { FlightOptionsContainer } from '@elara/client-core/containers/FlightOptionsContainer';
import type { HotelsContainer } from '@elara/client-core/containers/HotelsContainer';
import type { PlacesContainer } from '@elara/client-core/containers/PlacesContainer';
import type { RoomOptionsContainer } from '@elara/client-core/containers/RoomOptionsContainer';
import type { FlightOptionModel } from '@elara/client-core/models/FlightOptionModel';
import type { RecommendationType } from '@elara/domain/models/schemas/widget-schemas';

import { roomShortlistMetadata } from '@elara/client-core/lib/room-utils';
import { cn, parseDocumentId, pluralize } from '@elara/client-core/lib/utils';

import { useRefreshPlace } from '#lib/use-refresh-place.js';
import { useApp, useTripsState } from '#states/hooks.js';
import {
  Button,
  getFallbackCoverImageIcon,
  IconSizes,
  ItineraryButton,
  MaterialIcon,
  ShortlistButton,
  useMessageRef,
} from '#ui';
import { DestinationCard } from '#ui/cards/DestinationCard.js';
import { BASE_DETAIL_CARD_CONTENT_CLASSES, FEATURE_COVER_CLASSES } from '#ui/cards/DetailCardLayout.js';
import { FlightActions, FlightCard } from '#ui/cards/FlightCard.js';
import { HotelCard } from '#ui/cards/HotelCard.js';
import { PlaceCard } from '#ui/cards/PlaceCard.js';
import { RoomCard } from '#ui/cards/RoomCard.js';
import { RoomPickBadge, RoomPickReason } from '#ui/cards/RoomPick.js';
import { CarouselRow } from '#ui/CarouselRow.js';

import { SimpleMarkdown } from './SimpleMarkdown.js';
import { ViewAllRoomResultsButton } from './ViewAllRoomResultsButton.js';

export interface RecommendationWidgetProps {
  type: RecommendationType;
  referenceId?: string;
  /** Normalised by the widget spec transform — always the full set, even for a single entity. */
  referenceIds?: string[];
  body?: string;
  className?: string;
}

/**
 * The chrome a recommendation card sits in inside a chat message — the cards themselves are surface-
 * agnostic (the shortlist list and the preview popover supply their own).
 *
 * Undressed at rest: no border, no surface, only a shadow on hover. A bordered, filled panel around
 * each of several consecutive recommendations turned a message into a stack of boxes; now that the
 * cover photo carries the card, the photo and the text hold together without a frame drawn round them.
 * `overflow-hidden` went with the border — the cover rounds its own corners, so there is nothing left
 * to clip, and clipping would only catch a focus ring on a child control.
 */
const RECOMMENDATION_CARD_CLASSES = 'relative rounded-md w-full';

/**
 * The lift-on-hover that says "this whole thing is one button".
 *
 * It is on both sizes because on both it is TRUE: `DetailCardLayout` opens the guide from the card
 * surface itself, and the parts with a job of their own — the action buttons — are excluded by the
 * surface handler rather than by leaving the halo off.
 *
 * The surface is a PSEUDO-ELEMENT at `-inset-3`, not a background, so the white and its shadow extend
 * a little PAST the content instead of stopping hard against the photo's edge — the card reads as
 * picked up rather than filled in. A real background cannot do that without padding the card and
 * pulling it back with a negative margin, which under `box-sizing: border-box` shrinks the cover
 * instead of growing the box. `-z-10` keeps it behind the content; the card has no surface of its own
 * for it to hide behind. It grows out as it fades in — `inset-0` at rest, `-inset-3` on hover — because
 * fading a full-size panel in made the card look covered rather than lifted.
 */
const RECOMMENDATION_CARD_HOVER_HALO = cn(
  'before:absolute before:-z-10 before:rounded-lg before:bg-background before:shadow-md',
  'before:inset-0 before:opacity-0 before:transition-[inset,opacity] before:duration-300 before:ease-out',
  'hover:before:-inset-3 hover:before:opacity-100',
);

/** The chrome for one card: the shared surface plus the whole-card hover lift. */
const recommendationCardClasses = (extra?: string): string =>
  cn(RECOMMENDATION_CARD_CLASSES, RECOMMENDATION_CARD_HOVER_HALO, extra);

/** The two shapes a recommendation card takes: one entity, or one of several in a row. */
type RecommendationCardSize = 'lg' | 'feature';

/**
 * The shortlist heart's size. Shrunk only on a `feature` card, where it sits ON a 256px cover and a
 * 40px target takes the photo over; `lg` puts it beside the title at the size every other surface uses.
 */
const shortlistSize = (size: RecommendationCardSize) => (size === 'feature' ? ('icon-xs' as const) : undefined);

/**
 * The silhouette a recommendation occupies before it resolves, and if it never does. Both states
 * borrow the real card's geometry from `DetailCardLayout` — the same cover box, the same content
 * column — so a row of cards does not reflow as entities arrive one by one, and a failure leaves a
 * gap the size of the thing that failed rather than a stray line of text.
 *
 * `lg` is the content with the photo strip beneath it; `feature` is the 6:4 cover band above it.
 */
function RecommendationCardFrame({
  size,
  cover,
  className,
  children,
}: {
  size: 'lg' | 'feature';
  cover: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}): React.ReactNode {
  if (size === 'feature') {
    return (
      <div className={cn('flex w-full flex-col', className)}>
        <div className={cn(FEATURE_COVER_CLASSES, 'overflow-hidden bg-background-quaternary')}>{cover}</div>
        <div className={cn(BASE_DETAIL_CARD_CONTENT_CLASSES, 'gap-2 px-0')}>{children}</div>
      </div>
    );
  }
  // The `lg` card leads with its title and facts and puts the photos BELOW them, so the placeholder
  // does too — the whole point of borrowing the card's geometry is that nothing moves when the entity
  // resolves.
  //
  // `h-32` is the height `ImageGalleryWidget`'s inline strip gives its items, and matching it exactly
  // is the difference between reserving the right space and reserving three times too much: an
  // `aspect-video` band across a `max-w-2xl` message is ~378px, so the card visibly collapsed the
  // moment the photos arrived.
  return (
    <div className={cn('flex w-full flex-col gap-3', className)}>
      <div className="flex flex-col gap-1">{children}</div>
      <div className="h-32 w-full overflow-hidden rounded-md bg-background-quaternary">{cover}</div>
    </div>
  );
}

/** A pulsing bar standing in for a line of text. */
function SkeletonLine({ className }: { className?: string }): React.ReactNode {
  return <div className={cn('h-3 animate-pulse rounded bg-background-tertiary', className)} />;
}

/**
 * The degraded path, and only that (ELARA-1071): the entity's Firestore read failed, so there is no
 * card to draw. Legacy widgets carry the assistant's prose and can fall back to showing just that;
 * once ELARA-1053 stops widgets carrying a `body` at all, every failure takes the card-shaped path.
 *
 * It deliberately keeps the card's footprint instead of collapsing to a sentence. A carousel of five
 * where one failed should still read as five things, with one that did not load — collapsing it
 * reflows the row and makes the failure look like the recommendation was never there.
 */
export function RecommendationFallback({
  body,
  className,
  size = 'lg',
  type,
}: {
  body?: string;
  className?: string;
  size?: 'lg' | 'feature';
  type?: RecommendationType;
}): React.ReactNode {
  if (body) return <SimpleMarkdown text={body} className={className} />;
  return (
    <RecommendationCardFrame
      size={size}
      className={className}
      cover={
        <div className="flex h-full w-full items-center justify-center" aria-hidden="true">
          <MaterialIcon
            icon={getFallbackCoverImageIcon(type ?? '')}
            size={IconSizes.md}
            className="text-muted-foreground/40"
            weight="light"
          />
        </div>
      }
    >
      <p className="m-0 text-sm text-muted-foreground">Couldn&apos;t load this {type ?? 'recommendation'}.</p>
    </RecommendationCardFrame>
  );
}

/** Card plus the assistant's markdown prose beneath it — the shape every resolved recommendation takes. */
function RecommendationLayout({
  body,
  className,
  children,
}: {
  body?: string;
  className?: string;
  children: React.ReactNode;
}): React.ReactNode {
  return (
    // `my-2` matches `RecommendationCarousel`: a card is its own block, and the message stacks its
    // parts on a `gap-1` that is right for consecutive paragraphs and too tight against a card —
    // without it the card reads as belonging to the sentence directly above it.
    <div className={cn('@container mb-4 flex w-full flex-col gap-3', className)}>
      {children}
      {body && <SimpleMarkdown text={body} />}
    </div>
  );
}

export function RecommendationWidgetSkeleton({
  size = 'lg',
  className,
}: {
  size?: 'lg' | 'feature';
  className?: string;
}): React.ReactNode {
  return (
    <RecommendationCardFrame
      size={size}
      className={className}
      cover={<div className="h-full w-full animate-pulse bg-background-tertiary" />}
    >
      {/* A title and two lines beneath it — the description on a `feature` card, the facts rows on an
          `lg` one, which run to about the same height either way. */}
      <SkeletonLine className="h-5 w-1/2" />
      <SkeletonLine className="w-full" />
      <SkeletonLine className="w-4/5" />
      <span className="sr-only">Loading recommendation…</span>
    </RecommendationCardFrame>
  );
}

interface SubRecommendationProps {
  referenceId: string;
  id: string;
  body?: string;
  className?: string;
  /** `lg` for a lone recommendation, `feature` for a card in the carousel. */
  size?: 'lg' | 'feature';
  /** Rooms only: leave the hotel off the card, because the carousel's label names it. */
  hideHotel?: boolean;
}

/**
 * The hotel every room in a carousel is at, once all of them have loaded: the carousel's label names it
 * ("4 recommended rooms at Conrad New York") so each card need not. Undefined for other types, for rooms
 * at different hotels, or while any room is still loading — the label then counts rooms and each card
 * keeps its hotel. Reads the same cached room handles the cards render from.
 */
function useSharedRoomHotelName(type: RecommendationType, ids: string[]): string | undefined {
  const tripsState = useTripsState();
  const { features } = useApp('features');
  const { roomOptions } = features as { roomOptions: RoomOptionsContainer };
  const tripId = tripsState.current?.id;
  if (type !== 'room' || ids.length < 2 || !tripId) return;

  const names = new Set<string>();
  for (const entityId of ids) {
    const handle = roomOptions.ClientRoomOption.get({ tripId, id: parseDocumentId(entityId) ?? entityId });
    const name = handle.isReady ? handle.data?.hotel?.name : undefined;
    if (!name) return;
    names.add(name);
  }
  return names.size === 1 ? [...names][0] : undefined;
}

/**
 * RecommendationWidget displays information about a recommended destination, hotel, flight, room, or activity.
 * Each type is rendered by a dedicated sub-component that owns its own data loading.
 */
function renderRecommendation(type: RecommendationType, props: SubRecommendationProps): React.ReactNode {
  switch (type) {
    case 'flight':
      return <FlightRecommendation {...props} />;
    case 'hotel':
      return <HotelRecommendation {...props} />;
    case 'destination':
      return <DestinationRecommendation {...props} />;
    case 'room':
      return <RoomRecommendation {...props} />;
    case 'place':
      return <PlaceRecommendation {...props} />;
    default:
      return null;
  }
}

// Explicit return type: React 19's `ReactNode` includes `Promise<AwaitedReactNode>`, so an inferred
// one trips `promise-function-async` on any component that returns another component's output.
export function RecommendationWidget({
  type,
  referenceId,
  referenceIds,
  body,
  className,
}: RecommendationWidgetProps): React.ReactNode {
  // The spec transform normalises both id shapes into `referenceIds`; the fallbacks are for a caller
  // that builds props by hand (tests, the debug inspector) rather than going through it.
  const ids = referenceIds?.length ? referenceIds : referenceId ? [referenceId] : [];
  const sharedHotelName = useSharedRoomHotelName(type, ids);
  if (ids.length === 0) return null;

  // ONE entity keeps the side-by-side card: stacked at full message width, its photo dominates the
  // feed, which is the reason `lg` became a row in the first place. SEVERAL become a carousel of
  // stacked `feature` cards, where each is narrow enough for a cover to be an asset rather than a wall.
  if (ids.length === 1) {
    const only = ids[0]!;
    return renderRecommendation(type, { referenceId: only, id: parseDocumentId(only) ?? only, body, className });
  }

  // Flights are the exception, and it is a real one rather than a special case for its own sake. The
  // `feature` card is built around a cover photo, and a flight has none — `FlightCard` branches on
  // `size === 'lg'` alone, so in a carousel slot it fell back to the compact row meant for shortlist
  // lists, squeezed to 256px. What distinguishes two flights is a grid of times, airports and prices
  // that needs the width. So they stack at full width instead, where they stay comparable down the
  // column the way search results are.
  if (type === 'flight') {
    return (
      <RecommendationLayout body={body} className={className}>
        {ids.map((entityId) => (
          <div key={entityId}>
            {renderRecommendation(type, { referenceId: entityId, id: parseDocumentId(entityId) ?? entityId })}
          </div>
        ))}
      </RecommendationLayout>
    );
  }

  return (
    // "4 recommended hotels" — the count is worth stating when the row hides some of them, and the
    // type noun says what is being counted without the reader inferring it from the cards. Rooms all at
    // one hotel name it there ("4 recommended rooms at Conrad New York") instead of on every card.
    <RecommendationCarousel
      body={body}
      className={className}
      label={`${ids.length} recommended ${pluralize(ids.length, type)}${sharedHotelName ? ` at ${sharedHotelName}` : ''}`}
      pinLabel={!!sharedHotelName}
      // Room cards are the presented few of a search that saved every room the hotel had (ELARA-1092).
      labelAction={
        type === 'room' ? <ViewAllRoomResultsButton roomOptionId={parseDocumentId(ids[0]!) ?? ids[0]!} /> : undefined
      }
    >
      {ids.map((entityId) => (
        <RecommendationCarouselItem key={entityId}>
          {renderRecommendation(type, {
            referenceId: entityId,
            id: parseDocumentId(entityId) ?? entityId,
            size: 'feature',
            hideHotel: !!sharedHotelName,
          })}
        </RecommendationCarouselItem>
      ))}
    </RecommendationCarousel>
  );
}

/**
 * The carousel shell, separated from the id resolution so it can be rendered against fixture cards
 * (Storybook) without a Firestore read behind every slot. Keeping it here rather than copying the
 * markup into a story is the point: the row's gap, snap and fade are design decisions, and a story
 * that reproduces them by hand stops being evidence the moment either side changes.
 */
export function RecommendationCarousel({
  body,
  className,
  label,
  pinLabel = false,
  labelAction,
  children,
}: {
  body?: string;
  className?: string;
  /** Sits opposite the arrows, e.g. "4 recommended hotels". Only visible when the row scrolls, unless pinned. */
  label?: React.ReactNode;
  /** Keep the label when the row does not scroll: it names something the cards leave out (the rooms' hotel). */
  pinLabel?: boolean;
  /** Beside the label, e.g. "View all rooms" for room cards; shown even when the row does not scroll. */
  labelAction?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactNode {
  return (
    // `my-2` for the same reason as `RecommendationLayout` — see the note there.
    <div className={cn('@container mb-4 flex w-full flex-col gap-3', className)}>
      <CarouselRow
        arrows="header"
        label="recommendations"
        // The chat sits on the app body, which `index.css` paints `bg-background-secondary`; the edge
        // fade has to dissolve into THAT, not the white a card happens to use.
        fadeFrom="from-background-secondary"
        header={label ? <span className="text-sm font-medium">{label}</span> : undefined}
        persistentHeader={pinLabel}
        headerAction={labelAction}
        contentClassName="snap-x snap-mandatory items-stretch"
      >
        {children}
      </CarouselRow>
      {/* `body` is the assistant's prose about the SET, so it renders once beneath the row rather than
          repeating under every card — which also keeps the cards the same height. */}
      {body && <SimpleMarkdown text={body} />}
    </div>
  );
}

/** One slot in the row. Fixed width and `shrink-0` are what make `CarouselRow`'s stride measurable. */
export function RecommendationCarouselItem({ children }: { children: React.ReactNode }): React.ReactNode {
  // `flex` makes the card a flex item of this slot, so it stretches to the slot's height instead of
  // stopping at its own content. The row already stretches every slot to the tallest card; without
  // this the card inside a SHORT one (a hotel with no Elara Benefits row, say) kept its content height,
  // and its hover halo — which tracks the card — ended above its neighbours' bottom edge.
  return <div className="flex w-64 shrink-0 snap-start @sm:w-72">{children}</div>;
}

// `body` is unused on the happy path — flight cards have always ignored the assistant's prose — but the
// failure path still offers it, so a legacy widget degrades to its text rather than to nothing.
function FlightRecommendation({ id, body, className, size = 'lg' }: SubRecommendationProps) {
  const tripsState = useTripsState();
  const tripId = tripsState.current?.id;
  const { features } = useApp('features');
  const { flightOptions } = features as { flightOptions: FlightOptionsContainer };
  const messageRef = useMessageRef();

  const handle = flightOptions.ClientFlightOption.get({ tripId, id });
  if (!handle.isReady) return <RecommendationWidgetSkeleton size={size} className={className} />;
  // Without this the `as` below casts an undefined straight into `FlightCard`. A flight is the one
  // type whose read is trip-scoped, so it is also the one most likely to come back empty.
  if (handle.error ?? !handle.data)
    return <RecommendationFallback body={body} className={className} size={size} type="flight" />;

  const flightOption = handle.data as FlightOptionModel;

  return (
    <div className="@container w-full">
      <FlightCard
        flightOption={flightOption}
        actions={<FlightActions flightOption={flightOption} messageRef={messageRef} variant="icon" />}
        onClick={() => tripsState.showGuide('flight', flightOption.id)}
        size={size}
        // A standing white surface, unlike the other types. Those are anchored by a cover photo that
        // gives the card an edge of its own; a flight is a grid of times and prices, and with no
        // background it read as loose text on the page rather than as a card — most visible in a stack
        // of several, where nothing said where one flight ended and the next began. The hover halo
        // still sits behind it and extends past it, so the hover treatment is unchanged.
        className={recommendationCardClasses('bg-background/80')}
      />
    </div>
  );
}

function HotelRecommendation({ referenceId, id, body, className, size = 'lg' }: SubRecommendationProps) {
  const tripsState = useTripsState();
  const { features } = useApp('features');
  const { hotels } = features as { hotels: HotelsContainer };
  const messageRef = useMessageRef();

  const handle = hotels.ClientHotel.get({ id });
  if (!handle.isReady) return <RecommendationWidgetSkeleton size={size} className={className} />;
  if (handle.error ?? !handle.data)
    return <RecommendationFallback body={body} className={className} size={size} type="hotel" />;
  const hotel = handle.data;
  const title = hotel.name ?? 'Hotel';
  const showGuide = () => tripsState.showGuide('hotel', referenceId);

  return (
    <RecommendationLayout body={body} className={className}>
      <HotelCard
        hotel={hotel}
        size={size}
        className={recommendationCardClasses()}
        actions={
          <ShortlistButton
            itemType="hotel"
            referenceId={referenceId}
            title={title}
            messageRef={messageRef}
            variant="icon"
            size={shortlistSize(size)}
          />
        }
        onClick={showGuide}
      />
    </RecommendationLayout>
  );
}

function DestinationRecommendation({ referenceId, id, body, className, size = 'lg' }: SubRecommendationProps) {
  const tripsState = useTripsState();
  const { features } = useApp('features');
  const { destinations } = features as { destinations: DestinationsContainer };
  const messageRef = useMessageRef();

  const handle = destinations.ClientDestination.get({ id });
  if (!handle.isReady) return <RecommendationWidgetSkeleton size={size} className={className} />;
  if (handle.error ?? !handle.data)
    return <RecommendationFallback body={body} className={className} size={size} type="destination" />;
  const destination = handle.data;
  const title = destination.displayName;
  const showGuide = () => tripsState.showGuide('destination', referenceId);

  return (
    <RecommendationLayout body={body} className={className}>
      <DestinationCard
        destination={destination}
        size={size}
        className={recommendationCardClasses()}
        actions={
          <ShortlistButton
            itemType="destination"
            referenceId={referenceId}
            title={title}
            messageRef={messageRef}
            variant="icon"
            size={shortlistSize(size)}
          />
        }
        onClick={showGuide}
      />
    </RecommendationLayout>
  );
}

function RoomRecommendation({
  referenceId,
  id,
  body,
  className,
  size = 'lg',
  hideHotel = false,
}: SubRecommendationProps) {
  const tripsState = useTripsState();
  const tripId = tripsState.current?.id;
  const { features } = useApp('features');
  const { roomOptions } = features as { roomOptions: RoomOptionsContainer };
  const messageRef = useMessageRef();

  if (!tripId) return null;

  const handle = roomOptions.ClientRoomOption.get({ tripId, id });
  if (!handle.isReady) return <RecommendationWidgetSkeleton size={size} className={className} />;
  if (handle.error ?? !handle.data)
    return <RecommendationFallback body={body} className={className} size={size} type="room" />;
  const roomOption = handle.data;
  const title = roomOption.displayName;
  const showGuide = () => tripsState.showGuide('room', referenceId);
  // NO action row on this card, deliberately: adding a room to a trip or booking it commits to a
  // specific RATE, and the card shows one lead price with no way to see the others. Sending the guest
  // to the guide first is what puts the rates in front of them before they choose. The shortlist heart
  // stays — saving a room to look at later commits to nothing.
  //
  // The card prices the room's LEAD rate: the party the search asked about first, only rates still
  // bookable, never a gated rate (military, members) while an open one exists. It used to price the
  // cheapest rate of any party, sold out or not, which put a dead military rate on a family's suite and
  // read the whole card "Sold out" (Andaz 5th Avenue, 2026-10-04). The cheapest rate is the fallback
  // only once every rate is gone, so a sold-out card still shows what it cost. The heart saves the room,
  // not this rate: the shortlist card leads with the room's lowest bookable rate on its own.
  //
  // A room Elara picked is priced on the PICKED rate instead, and says why (`RoomPick`), as the hotel
  // guide's Find rooms grid shows it: the reason is about that rate ("breakfast for two is included"),
  // so the price beside it must be that rate's.
  const pick = roomOption.recommendedRate;
  const recommendation = pick?.recommendation;
  const pricedRate = pick ?? roomOption.leadRate ?? roomOption.cheapestRate;
  return (
    <RecommendationLayout body={body} className={className}>
      <RoomCard
        room={roomOption}
        size={size}
        omitFacts={hideHotel ? ['hotel'] : []}
        // The identity the card is pricing, so availability and the party resolve through that exact
        // rate. Mobile passes the same pair for the same reason.
        productCode={pricedRate?.productCode}
        occupancy={pricedRate?.occupancy}
        // "Book 2" beside the name when the party needs more than one of the room; the price line then totals them.
        quantity={roomOption.roomsToBook(pricedRate?.occupancy)}
        // These rooms come from the search the traveller just asked for, so a price for that party needs no
        // label; one priced for another party still says so.
        pricePartyLabel="when-different"
        // On the carousel's `feature` card; `lg` draws neither (see `RoomCard`).
        coverBadge={recommendation && <RoomPickBadge recommendation={recommendation} />}
        note={recommendation && <RoomPickReason recommendation={recommendation} />}
        className={recommendationCardClasses()}
        actions={
          <>
            {/* The way IN to the rates, since the card deliberately carries no add or book action. A
                ghost button because it is a route rather than a commitment, and labelled because "see
                the other prices" is not a thing a glyph says. `lg` only: a 256px carousel card has no
                room for words beside the heart, and its whole surface already opens the guide. */}
            {size === 'lg' && (
              // Hidden on a narrow viewport: a labelled button and the heart together do not fit
              // beside a room name, and of the two the heart is the one that must survive — it is the
              // only action here, where "View rates" duplicates what tapping the card already does.
              <Button variant="ghost" onClick={showGuide} className="hidden sm:inline-flex">
                View rates
              </Button>
            )}
            <ShortlistButton
              itemType="room"
              referenceId={referenceId}
              title={title}
              metadata={roomShortlistMetadata(roomOption)}
              messageRef={messageRef}
              variant="icon"
              size={shortlistSize(size)}
            />
          </>
        }
        onClick={showGuide}
      />
    </RecommendationLayout>
  );
}

function PlaceRecommendation({ referenceId, id, body, className, size = 'lg' }: SubRecommendationProps) {
  const tripsState = useTripsState();
  const { features } = useApp('features');
  const { places } = features as { places: PlacesContainer };
  const messageRef = useMessageRef();

  // Kept here as well as in `PlaceCard`: this fires while the place is still loading (or failing), so
  // a stale copy gets refreshed even when no card ever renders.
  useRefreshPlace(id);

  const handle = places.ClientPlace.get({ id });
  const place = handle.data;

  if (!handle.isReady) return <RecommendationWidgetSkeleton size={size} className={className} />;
  if (handle.error ?? !place)
    return <RecommendationFallback body={body} className={className} size={size} type="place" />;
  const title = place.displayName;
  const showGuide = () => tripsState.showGuide('place', referenceId);

  return (
    <RecommendationLayout body={body} className={className}>
      <PlaceCard
        place={place}
        size={size}
        className={recommendationCardClasses()}
        // Both as icons in the corner. A place has one thing to do with it and the glyph carries it;
        // a labelled button below the gallery is the room card's treatment, where "Book now" genuinely
        // needs its words. Heart first, then itinerary — the order `FlightActions` already uses, so a
        // card of either type presents the same two controls in the same places.
        actions={
          <>
            <ShortlistButton
              itemType="place"
              referenceId={referenceId}
              title={title}
              messageRef={messageRef}
              variant="icon"
              size={shortlistSize(size)}
            />
            <ItineraryButton
              itemType="place"
              referenceId={referenceId}
              title={title}
              messageRef={messageRef}
              variant="icon"
              size={shortlistSize(size)}
            />
          </>
        }
        onClick={showGuide}
      />
    </RecommendationLayout>
  );
}
